import { Icon } from "@iconify/react";
import { LightTheme, useTheme } from "../../../hooks/useTheme";
import { Colors } from "../../../utils/colors";
import SwitchToggleItem from "../../SwitchToggleItem";
import {
  Event,
  PromoCode,
  PromoCodeType,
  TicketV2,
  NotificationType,
} from "@markit/common.types";
import NumberInput from "../../NumberInput";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  fetchAllFreeTicketsWithAlias,
  findFreeTicketAlias,
} from "../../../utils/eventUtils/eventUtils";
import { API } from "../../../API";
import { MixpanelContext } from "../../../context/AnalyticsService";
import PopupModalContainer from "../../Containers/PopupModalContainer";
import CustomTextField from "../../CustomTextField";
import RectangleButton from "../../Buttons/RectangleButton";
import AlertContainer from "../../Containers/AlertContainer";
import useAsyncEffect from "../../../hooks/useAsyncEffect";
import { showNotificationBanner } from "../../../utils/notificationUtils";
import { useDispatch } from "react-redux";
import { HorizontalDivider } from "../../Dividers/HorizontalDivider";
import { useOnMount } from "../../../hooks/useOnMount";
import { productItemLabel } from "@markit/common.utils";

const FREE_PROMO_MAX_USES = 200;
const DISCOUNT_PROMO_MAX_USES = 100000;

type PromoCodeModalProps = {
  setIsPromoModalVisible: (promoModalVisible: boolean) => void;
  event: Event;
  item: PromoCode | TicketV2 | undefined;
  setItem: (item: PromoCode | TicketV2 | undefined) => void;
};

export const PromoCodeModal = (props: PromoCodeModalProps) => {
  const { setIsPromoModalVisible, event, item, setItem } = props;
  const { theme } = useTheme();
  const mixpanel = useContext(MixpanelContext);
  const dispatch = useDispatch();
  const [selectedTicketIndex, setSelectedTicketIndex] = useState<number>(0);
  const [promoCodeModalText, setPromoCodeModalText] = useState<string>("");
  const [promoModalDiscount, setPromoModalDiscount] = useState<
    number | undefined
  >(
    item
      ? (item as PromoCode).type !== undefined
        ? (item as PromoCode).type === PromoCodeType.DOLLARS_OFF
          ? (item as PromoCode).constants[0] / 100
          : (item as PromoCode).constants[0]
        : 100
      : 20
  );
  const [promoModalMinUses, setPromoModalMinUses] = useState<
    number | undefined
  >(1);
  const [promoCodeType, setPromoCodeType] = useState<PromoCodeType | undefined>(
    undefined
  );
  const [promoModalFreeTicket, setPromoModalFreeTicket] =
    useState<boolean>(false);

  // for editing free tickets
  const [usedFreeTickets, setUsedFreeTickets] = useState<TicketV2[]>([]);
  const [promoModalUses, setPromoModalUses] = useState<number | undefined>(1);

  const [alertText, setAlertText] = useState({ heading: "", subHeading: "" });

  const styles = {
    textLabel: {
      fontSize: 14,
      fontWeight: 600,
    },
    inputContainer: { padding: "10px 14px", borderRadius: 12, width: 100 },
  };

  const isEditingPromo = useMemo(() => item !== undefined, [item]);

  const itemLabel = useMemo(
    () => productItemLabel(event.eventType),
    [event.eventType]
  );

  useOnMount(() => {
    if (isEditingPromo && item) {
      setPromoCodeModalText(item.alias);
      setPromoCodeType((item as PromoCode).type);
      // Free promo code case
      if ((item as PromoCode).type === undefined) {
        setPromoModalFreeTicket(true);
        const foundCustomTicketIndex = event.customTickets.findIndex(
          (customTix) => customTix.id === (item as TicketV2).customTicketId
        );
        if (foundCustomTicketIndex !== -1) {
          setSelectedTicketIndex(foundCustomTicketIndex);
        }
      }
    }
  });

  const resetFields = useCallback(() => {
    setItem(undefined);
    setIsPromoModalVisible(false);
    setPromoCodeModalText("");
    setPromoModalDiscount(20);
    setPromoModalUses(1);
    setPromoModalFreeTicket(false);
  }, [setIsPromoModalVisible, setItem]);

  const isPromoCode = useMemo(() => {
    if (item) {
      return (item as PromoCode).type !== undefined;
    } else {
      return false;
    }
  }, [item]);

  const isFreePromo = useMemo(
    () => (item ? (item as PromoCode).type === undefined : false),
    [item]
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useAsyncEffect(async () => {
    if (item) {
      if (isFreePromo) {
        const freeTickets = await fetchAllFreeTicketsWithAlias(event.id);
        const filtered = freeTickets.filter((ticket) => {
          return ticket.alias === item.alias;
        });
        setPromoModalUses(filtered.length);
        const usedTix = filtered.filter((ticket) => {
          return ticket.role.type !== "unassigned";
        });
        setUsedFreeTickets(usedTix);
        setPromoModalMinUses(usedTix.length);
      } else {
        setPromoModalUses((item as PromoCode).maxUseCount);
        setPromoModalMinUses((item as PromoCode).usedCount);
      }
    }
  }, [event, item, isFreePromo]);

  useEffect(() => {
    if (!isEditingPromo) {
      if (promoCodeType === PromoCodeType.PERCENT_OFF) {
        setPromoModalDiscount(20);
      } else if (promoCodeType === PromoCodeType.DOLLARS_OFF) {
        setPromoModalDiscount(10);
      }
    }
  }, [promoCodeType, isEditingPromo]);

  const promoAlreadyUsed = useMemo(() => {
    if (item) {
      if (isPromoCode) {
        const promoCode = item as PromoCode;
        return promoCode.usedCount > 0;
      } else {
        return usedFreeTickets.length > 0;
      }
    }
    return false;
  }, [item, usedFreeTickets, isPromoCode]);

  const savePromoCodePress = useCallback(async () => {
    const codeNameUppercase = promoCodeModalText.toUpperCase();
    const found = event.promoCodes.find(
      (code) => code.alias && code.alias.toUpperCase() === codeNameUppercase
    );

    const foundTicketName = await findFreeTicketAlias(
      event.id,
      codeNameUppercase
    );

    if (found || foundTicketName[0]) {
      setAlertText({
        heading: "Promo code with this name already exists.",
        subHeading: "Please choose a new name.",
      });
      return false;
    }

    if (promoModalFreeTicket) {
      API.promoCodes
        .createFreeTicketCode({
          eventId: event.id,
          uid: event.createdBy,
          customTicketId: event.customTickets[selectedTicketIndex].id,
          alias: codeNameUppercase,
          maxUseCount: promoModalUses ?? 1,
        })
        .then((response) => {
          if (response.freeTicketCodes.length !== promoModalUses) {
            setAlertText({
              heading: "Uses exceeds available ticket quantity.",
              subHeading: "Please try again.",
            });
            return false;
          } else {
            mixpanel.track("Generated Free Ticket Code", {
              event_id: event.id,
              ticket_id: response.freeTicketCodes[0].id,
              type: event.eventType,
            });
          }
        })
        .catch(() => {
          setAlertText({
            heading: "Something went wrong.",
            subHeading: "Please try again.",
          });
          return false;
        });
    } else {
      await API.promoCodes
        .createPromoCode({
          eventId: event.id,
          type:
            promoCodeType !== undefined
              ? promoCodeType
              : PromoCodeType.PERCENT_OFF,
          constants: [promoModalDiscount ?? 1],
          redeemedBy: "",
          alias: codeNameUppercase,
          maxUseCount: promoModalUses ?? 1,
        })
        .then((response) => {
          if (response.promoCodes.length !== 1) {
            setAlertText({
              heading: "Promo Code Created Incorrectly.",
              subHeading: "Please try again.",
            });
            return false;
          } else {
            mixpanel.track("Generated Named Promo Code", {
              event_id: event.id,
              isUnlimited: false,
            });
          }
        })
        .catch(() => {
          setAlertText({
            heading: "Something went wrong.",
            subHeading: "Please try again.",
          });
          return false;
        });
    }
    return true;
  }, [
    promoCodeModalText,
    event.promoCodes,
    event.id,
    event.createdBy,
    event.customTickets,
    event.eventType,
    promoModalFreeTicket,
    selectedTicketIndex,
    promoModalUses,
    mixpanel,
    promoCodeType,
    promoModalDiscount,
  ]);

  const saveEditsPromoCodePress = useCallback(async () => {
    if (item && promoModalDiscount && promoModalUses) {
      await API.promoCodes
        .updatePromoCode({
          eventId: event.id,
          promoCodeId: item.id,
          updatedAlias: promoCodeModalText,
          updatedDiscount: [promoModalDiscount],
          updatedMaxUseCount: promoModalUses,
        })
        .catch((e) => {
          console.log(e.message);
        });
    }
  }, [item, promoModalDiscount, promoModalUses, event.id, promoCodeModalText]);

  const saveEditsFreeTicketPress = useCallback(async () => {
    if (promoModalUses && item) {
      await API.promoCodes
        .updateFreePromo({
          eventId: event.id,
          existingAlias: item.alias,
          updatedAlias: promoCodeModalText,
          updatedMaxUseCount: promoModalUses,
        })
        .catch((e) => {
          console.log(e.message);
        });
    }
  }, [promoModalUses, item, event.id, promoCodeModalText]);

  const onSavePress = useCallback(async () => {
    if (!promoCodeModalText.trim()) {
      setAlertText({
        heading: "Promo code name is required",
        subHeading: "",
      });
    } else if (/\s/.test(promoCodeModalText)) {
      setAlertText({
        heading: "Promo codes can't include spaces",
        subHeading: "",
      });
    } else if (promoCodeType === undefined && !promoModalFreeTicket) {
      setAlertText({
        heading: "Please select a promo code type",
        subHeading: "",
      });
    } else if (
      promoModalUses === undefined ||
      !Number.isInteger(promoModalUses) ||
      promoModalUses < 1
    ) {
      setAlertText({
        heading: "Invalid number of uses",
        subHeading: "Uses must be a full integer greater than 0",
      });
    } else if (
      isEditingPromo &&
      promoModalMinUses &&
      promoModalUses < promoModalMinUses
    ) {
      setAlertText({
        heading: "Invalid number of uses",
        subHeading: "Uses cannot be less than the current minimum uses",
      });
    } else if (
      promoModalUses >
      (isFreePromo || promoModalFreeTicket
        ? FREE_PROMO_MAX_USES
        : DISCOUNT_PROMO_MAX_USES)
    ) {
      setAlertText({
        heading: "Invalid number of uses",
        subHeading: "Uses must be less than the maximum number of uses",
      });
    } else if (
      !promoModalFreeTicket &&
      (promoModalDiscount === undefined ||
        promoModalDiscount < 1 ||
        promoModalDiscount >
          (promoCodeType === PromoCodeType.PERCENT_OFF ? 99 : 1000) ||
        (promoCodeType === PromoCodeType.PERCENT_OFF &&
          !Number.isInteger(promoModalDiscount)))
    ) {
      setAlertText({
        heading: "Invalid discount",
        subHeading:
          promoCodeType === PromoCodeType.PERCENT_OFF
            ? "Discount must be a full integer between 1% and 99%"
            : "Discount must be between 1 and 1000",
      });
    } else {
      if (isEditingPromo) {
        if (isFreePromo) {
          await saveEditsFreeTicketPress();
        } else {
          await saveEditsPromoCodePress();
        }
      } else {
        const success = await savePromoCodePress();
        if (!success) {
          return;
        }
      }
      showNotificationBanner(
        dispatch,
        "Promo Code " + (isEditingPromo ? "Updated!" : "Created!"),
        NotificationType.AFFIRMATIVE
      );
      resetFields();
    }
  }, [
    promoCodeModalText,
    promoCodeType,
    promoModalFreeTicket,
    promoModalUses,
    isEditingPromo,
    promoModalMinUses,
    promoModalDiscount,
    dispatch,
    resetFields,
    isFreePromo,
    saveEditsFreeTicketPress,
    saveEditsPromoCodePress,
    savePromoCodePress,
  ]);

  return (
    <PopupModalContainer
      headerComp={isEditingPromo ? "Edit Promo Code" : "New Promo Code"}
      headerLeftIcon={
        !isEditingPromo ? (
          <div style={{ paddingInline: "14px", marginBottom: "10px" }}>
            <Icon
              icon={"ion:pricetags"}
              height={35}
              style={{
                color: Colors.BLACK,
              }}
            />
          </div>
        ) : null
      }
      noExit={true}
      valueComp={
        <>
          <div className="ColumnNormal" style={{ gap: 14 }}>
            <div className="ColumnNormal" style={{ gap: 7 }}>
              <div className="AlignedRowSpaced">
                <span className="bodyMedium">Name</span>
                <span className="smallBodySubtext">
                  {promoCodeModalText.length}/30
                </span>
              </div>
              <CustomTextField
                value={promoCodeModalText}
                placeholder="Promo Code Name..."
                inputMode="text"
                borderRadius={12}
                backgroundColor={Colors.WHITE}
                onChange={(change: any) => {
                  setPromoCodeModalText(change.target.value);
                }}
                altPadding={14}
                altMarginBottom={0}
                maxLength={30}
              />
              <span
                className="smallBodySubtext"
                style={{ color: Colors.GRAY2 }}
              >
                No spaces or lower case letters
              </span>
            </div>
            <div>
              <SwitchToggleItem
                title={`Free ${itemLabel.singularCap}`}
                description=""
                toggleValue={promoModalFreeTicket}
                onChange={() => {
                  if (
                    !promoModalFreeTicket &&
                    promoModalUses &&
                    promoModalUses > 200
                  ) {
                    setPromoModalUses(200);
                  }
                  setPromoCodeType(undefined);
                  setPromoModalFreeTicket(!promoModalFreeTicket);
                }}
                theme={theme}
                disabled={isEditingPromo}
              />
            </div>
            {promoModalFreeTicket ? (
              <div
                className="ColumnNormal"
                style={{
                  marginLeft: 10,
                  gap: 7,
                  pointerEvents: isEditingPromo ? "none" : "all",
                  opacity: isEditingPromo ? 0.5 : 1,
                }}
              >
                {event.customTickets.map((customTicket, index) => (
                  <div
                    key={index}
                    className="AlignedRowSelect"
                    onClick={() => {
                      if (selectedTicketIndex !== index) {
                        setSelectedTicketIndex(index);
                      }
                    }}
                  >
                    <Icon
                      icon={
                        selectedTicketIndex === index
                          ? "mdi:radiobox-marked"
                          : "mdi:radiobox-blank"
                      }
                      height={24}
                      style={{
                        color:
                          theme === LightTheme ? Colors.BLACK : Colors.WHITE,
                        marginRight: 5,
                      }}
                    />
                    <div style={{ ...theme.PrimaryText, ...styles.textLabel }}>
                      {customTicket.label}
                    </div>
                  </div>
                ))}
              </div>
            ) : null}
            <SwitchToggleItem
              title="Percent Off"
              description="Make this a percent-off discount"
              toggleValue={promoCodeType === PromoCodeType.PERCENT_OFF}
              onChange={() => {
                setPromoModalFreeTicket(false);
                setPromoCodeType(
                  promoCodeType === PromoCodeType.PERCENT_OFF
                    ? undefined
                    : PromoCodeType.PERCENT_OFF
                );
              }}
              theme={theme}
              disabled={isEditingPromo}
            />
            {promoCodeType === PromoCodeType.PERCENT_OFF ? (
              <div className="AlignedRowSpaced">
                <div style={{ ...theme.PrimaryText, ...styles.textLabel }}>
                  Discount
                </div>
                <NumberInput
                  min={1}
                  max={99}
                  prefix={"%"}
                  value={promoModalDiscount}
                  onChange={setPromoModalDiscount}
                  disabled={isEditingPromo && (isFreePromo || promoAlreadyUsed)}
                  containerStyles={styles.inputContainer}
                />
              </div>
            ) : null}
            <SwitchToggleItem
              title="Fixed Amount"
              description="Make this a fixed-dollar amount discount"
              toggleValue={promoCodeType === PromoCodeType.DOLLARS_OFF}
              onChange={() => {
                setPromoModalFreeTicket(false);
                setPromoCodeType(
                  promoCodeType === PromoCodeType.DOLLARS_OFF
                    ? undefined
                    : PromoCodeType.DOLLARS_OFF
                );
              }}
              theme={theme}
              disabled={isEditingPromo}
            />
            {promoCodeType === PromoCodeType.DOLLARS_OFF ? (
              <div className="AlignedRowSpaced">
                <div style={{ ...theme.PrimaryText, ...styles.textLabel }}>
                  Discount
                </div>
                <NumberInput
                  min={1}
                  max={1000}
                  prefix={"$"}
                  value={promoModalDiscount}
                  onChange={(value) => {
                    const roundedValue = Number(value?.toFixed(2));
                    setPromoModalDiscount(roundedValue);
                  }}
                  disabled={isEditingPromo && (isFreePromo || promoAlreadyUsed)}
                  containerStyles={styles.inputContainer}
                  allowDecimal
                />
              </div>
            ) : null}
            <div className="AlignedRowSpaced">
              <div style={{ ...theme.PrimaryText, ...styles.textLabel }}>
                Set uses
              </div>
              <NumberInput
                min={promoModalMinUses ?? 1}
                max={
                  isFreePromo || promoModalFreeTicket
                    ? FREE_PROMO_MAX_USES
                    : DISCOUNT_PROMO_MAX_USES
                }
                value={promoModalUses}
                onChange={setPromoModalUses}
                containerStyles={styles.inputContainer}
              />
            </div>
          </div>
          <AlertContainer
            headerComp={alertText.heading}
            subHeaderComp={
              alertText.subHeading !== "" ? alertText.subHeading : undefined
            }
            theme={theme}
            closeModal={() => setAlertText({ heading: "", subHeading: "" })}
            hideModal={alertText.heading === "" && alertText.subHeading === ""}
          />
        </>
      }
      fixedBottomContent={
        <div className="ColumnNormal" style={{ gap: 14, paddingTop: 14 }}>
          <HorizontalDivider altMargin={0} />
          <div
            className="AlignedRowSpaced"
            style={{ paddingInline: 14, gap: 10 }}
          >
            <RectangleButton
              buttonLabel={"Cancel"}
              onPress={resetFields}
              altColor={Colors.WHITE1}
              altTextColor={Colors.BLACK}
              altPaddingVert={14}
              containerStyles={{ border: `0.5px solid ${Colors.GRAY2}` }}
            />
            <RectangleButton
              buttonLabel={isEditingPromo ? "Save" : "Create"}
              onPress={onSavePress}
              altColor={Colors.BLACK}
              altTextColor={Colors.WHITE}
              altPaddingVert={14}
              disabled={promoCodeModalText.trim().length === 0}
            />
          </div>
        </div>
      }
      theme={theme}
      closeModal={resetFields}
    />
  );
};
